/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AUTHENTICATION_CONNECTION,
  AuthenticationDispatchers,
  AuthenticationState,
  IdentityProvider,
  TENANT,
  getJwe,
  isLoggedIn,
  isPending,
} from '@b2x/authentication-library';

import { defineCustomElement as defineStandaloneElement } from '@oab/standalone/dist/components/oab-standalone';
import {
  connectionName as COMPONENT_REGISTRY_CONNECTION,
  ComponentsState,
  OwcComponentRegistryState,
  initializeComponentRegistry,
} from '@owc/component-registry-connection';
import { connectionName as AEM_CONNECTION, ConnectionAemEnvironmentState } from '@owc/connection-aem-environment';
import { TrackingEvent, getTrackingDispatchers } from '@owc/connection-aem-tracking';
import { connectionName as WKO_CONNECTION, WellKnownObjects } from '@owc/connection-wko-manager';
import { initializeLogger } from '@seamless/logger';
import { SeamlessCoreStore, initializeStore } from '@seamless/store';
import { Component, ComponentInterface, Host, JSX, Prop, State, h } from '@stencil/core';
import { Params, getParams, objectKeysToKebab } from '../../shared/helpers/params';
import { UserDecoder } from '../../shared/user/utilities/userDecoder';

defineStandaloneElement();
const OabBeServerSubdomainsMap: Record<string, string> = {
  DEV: 'test.',
  TEST: 'test.',
  INT: 'test.',
  PPROD: 'int.',
  PROD: '',
};

const onewebMarketMap = new Map<string, string>([
  ['en_XD', 'en_AE-DU'],
  ['en_XA', 'en_AE-AZ'],
]);

type SubscribeIfAvailableFn = (state: any, resolve: (value: unknown) => void, reject: (reason: any) => void) => void;

type ElementWithProps = Element & {
  user?: any;
  track?: (event: TrackingEvent) => void;
  login?: () => void;
  consents?: { name: string; enabled: boolean }[];
};

@Component({
  tag: 'oab-oneweb',
  styleUrl: 'oneweb.scss',
  shadow: false,
})
export class OnewebComponent implements ComponentInterface {
  @Prop() componentId!: string;
  @State() isAEM = false;
  @State() componentConfigs?: any;
  @State() isLoading = true;
  @State() onewebWKOS?: WellKnownObjects;
  @State() onewebENV?: ConnectionAemEnvironmentState;
  @State() store: SeamlessCoreStore = initializeStore();
  @State() logger = initializeLogger('oab-oneweb');
  @State() track?: (event: TrackingEvent) => void;
  @State() loginFn?: () => void;
  @State() env = 'TEST';
  @State() jwe?: string | null;
  @State() token?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @State() user?: any;
  @State() locale?: string;
  @State() params?: Params;

  iProps: any;

  getOabBeBaseUrl(): string {
    return `https://${OabBeServerSubdomainsMap[this.env]}api.oneweb.mercedes-benz.com/oab-be/v4`;
  }

  // temporarily using a large timeout because b2x is doing some tests on int that require this.
  async isSeamlessConnectionAvailable(name: string, timeout = 10000): Promise<any> {
    return this.store.getConnectionDispatchers(name, timeout);
  }

  async subscribeIfAvailable(name: string, subscribeFn: SubscribeIfAvailableFn): Promise<any> {
    return new Promise((resolve, reject) => {
      this.isSeamlessConnectionAvailable(name)
        .then(() => {
          this.store?.subscribe(name, (state) => subscribeFn(state, resolve, reject));
        })
        .catch(reject);
    });
  }

  fetchWkos(): Promise<WellKnownObjects> {
    return this.subscribeIfAvailable(WKO_CONNECTION, (state: WellKnownObjects, resolve) => {
      this.onewebWKOS = state;
      resolve(state);
    });
  }

  fetchENV(): Promise<ConnectionAemEnvironmentState> {
    return this.subscribeIfAvailable(AEM_CONNECTION, (state: ConnectionAemEnvironmentState, resolve) => {
      this.onewebENV = state;
      this.env = state.stage;
      resolve(state);
    });
  }

  fetchAuthentication(): Promise<AuthenticationState> {
    return this.subscribeIfAvailable(AUTHENTICATION_CONNECTION, (state: AuthenticationState, resolve) => {
      if (!isPending(state)) {
        if (isLoggedIn(state)) {
          this.jwe = getJwe(state);
        }
        resolve(state);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async fetchTracking(): Promise<(event: TrackingEvent) => void> {
    return new Promise((resolve, reject) => {
      const iReject = setTimeout(reject, 5);

      getTrackingDispatchers().then((dispatchers) => {
        clearTimeout(iReject);
        this.track = dispatchers?.pushTrackingEvent;
        resolve(this.track);
      });
    });
  }

  async fetchUser(language: string, market: string): Promise<any> {
    try {
      const authResponse = await fetch(`${this.getOabBeBaseUrl()}/ciam/auth?token=${this.jwe}`);

      if (!authResponse.ok) {
        throw Error(authResponse.statusText);
      }

      const auth = await authResponse.json();

      const userResponse = await fetch(
        `${this.getOabBeBaseUrl()}/api/customers/${auth.ciamId}?language=${language}&market=${market}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      const user = await userResponse.json();

      return { ...user, token: auth.token };
    } catch (error) {
      return null;
    }
  }

  async getComponentRegistryConfig<ComponentsRegistry = ComponentsState[]>(
    filter?: (components: ComponentsRegistry) => ComponentsRegistry,
  ): Promise<ComponentsRegistry> {
    const isComponentRegistryAvailable = await this.isSeamlessConnectionAvailable(COMPONENT_REGISTRY_CONNECTION);
    if (isComponentRegistryAvailable) {
      return new Promise((resolve) => {
        this.store.once(COMPONENT_REGISTRY_CONNECTION, (state: OwcComponentRegistryState) => {
          const components = Object.values(state.components) as unknown as ComponentsRegistry;
          resolve(filter ? filter(components) : components);
        });
      });
    }
    return Promise.resolve<ComponentsRegistry>([] as unknown as ComponentsRegistry);
  }

  async fetchLoginFn(): Promise<void> {
    return this.isSeamlessConnectionAvailable(AUTHENTICATION_CONNECTION).then(
      async (dispatchers: AuthenticationDispatchers) => {
        // iam-user-menu is registered?
        const iamMenuConfigs = await this.getComponentRegistryConfig((components) => {
          // at the moment the way to check if the component is registered is to check if payload keys exist
          // ... waiting for better days (alternative: document.querySelector('iam-user-menu'))
          return components.filter(
            (component) =>
              component.payload?.menuTitle &&
              (component.payload?.menuEntries ||
                component.payload?.menuEntriesBusiness ||
                component.payload?.menuEntriesPrivate),
          );
        });
        const isIamMenuOnThePage = !!iamMenuConfigs?.length;

        // Login callback
        // oab-fe is unaware of the parameters
        if (isIamMenuOnThePage) {
          this.loginFn = () => {
            const tenantId = location.hostname.includes('localhost') ? TENANT.CIAS : TENANT.ONEWEB;
            dispatchers.login({
              identityProvider: IdentityProvider.CIAS,
              tenantId,
            });
          };
        }
      },
    );
  }

  async componentWillLoad(): Promise<void> {
    this.logger = initializeLogger('oab-oneweb');
    this.store = initializeStore();

    if (this.componentId) {
      const componentRegistry = initializeComponentRegistry();
      componentRegistry.subscribe(this.componentId, (configs) => {
        this.componentConfigs = configs;
      });
    }

    this.isAEM = await this.isSeamlessConnectionAvailable(AEM_CONNECTION)
      .then(() => true)
      .catch(() => false);

    this.logger.log('isAEM: ', this.isAEM);

    //assuming you initialize your logger with the namespace 'seamless-logger'
    // localStorage.setItem('loggerDebugNamespaces', 'oab-oneweb');

    if (this.isAEM) {
      this.loadData();
    }
  }

  async loadData(): Promise<void> {
    await Promise.allSettled([
      this.fetchENV(),
      this.fetchWkos(),
      this.fetchAuthentication(),
      this.fetchTracking(),
      this.fetchLoginFn(),
    ]).then(async (values) => {
      this.logger.log('Promise all done', { values });

      // Query params
      this.params = objectKeysToKebab(getParams());

      const language = this.onewebWKOS?.Locale?.language;
      const country = this.onewebWKOS?.Locale?.country;

      if (language && country) {
        // Locale mapping
        const rawMarket = this.params?.locale || `${language.toLowerCase()}_${country.toUpperCase()}`;
        this.locale = onewebMarketMap.get(rawMarket) || rawMarket;

        // fetch user
        if (this.env && this.jwe) {
          const userRaw = await this.fetchUser(language, country);
          if (userRaw) {
            this.user = UserDecoder(userRaw);
          } else {
            this.logger.error('## Error fetching user ##');
          }
        }
      }
    });
    this.isLoading = false;
  }

  private updateStandaloneProps(): void {
    if (this.isLoading) {
      return;
    }
    const standaloneElement: ElementWithProps = document.getElementsByTagName('oab-standalone')?.[0];
    if (!standaloneElement) {
      clearTimeout(this.iProps);
      this.iProps = setTimeout(() => {
        this.updateStandaloneProps();
      }, 0);
      return;
    }
    if (this.user) {
      standaloneElement.user = this.user;
    }
    if (this.track) {
      standaloneElement.track = this.track;
    }
    if (this.loginFn) {
      standaloneElement.login = this.loginFn;
    }
    if (this.onewebWKOS?.ConsentList) {
      standaloneElement.consents = this.onewebWKOS.ConsentList;
    }
    this.logger.log('updateStandaloneProps: updated');
  }

  public componentDidRender(): void {
    this.updateStandaloneProps();
  }

  public render(): JSX.Element {
    const env = this.env;
    this.logger.log('isLoading: ', this.isLoading);
    this.logger.log('onewebENV: ', this.onewebENV);
    this.logger.log('onewebWKOS: ', this.onewebWKOS);
    this.logger.log('track: ', this.track); // Analytics function
    this.logger.log('loginFn: ', this.loginFn); // Login callback
    this.logger.log('params: ', this.params); // Query params
    this.logger.log('locale after mapping: ', this.locale); // Locale after mapping

    // TODO: !isAEM return error
    return (
      <Host>
        <div class="oab-screen">
          {this.isLoading && <wb-spinner theme="light" class="oab-spinner" />}
          {!this.isLoading && (
            <oab-standalone
              context={this.componentConfigs?.payload?.context || 'owpc'} // TODO how to do this for vans
              env={env}
              build={env?.toLowerCase()}
              language={this.onewebENV?.language}
              country={this.onewebENV?.country}
              direction={this.onewebENV?.languageDirection}
              {...this.params}
              locale={this.locale}
            />
          )}
        </div>
      </Host>
    );
  }
}
